// App
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';

// Components
import { LeftDesktopButton, LeftMobileButton, RightDesktopButton, RightMobileButton } from './Buttons';
import MemoizedWrapper from './MemoizedWrapper';
import NoResults from './NoResults';
import ScrollAlert from './ScrollAlert';

// Flux
import { useAppSelector } from '../flux/configureStore';

// Hooks
import { useButtons, useCarousel, useItemRefs, useScrollAlert, useScrollRefresh } from './hooks';

// Misc / utils
import { getActiveFilters } from '../utils/filterUtils';
import * as S from './styles';

// Types
import type { PotentialFilterTypes } from '../utils/filterUtils';
import type ComparablesModelType from '../models/Comparables';
import { usePrevious } from 'react-use';

export interface CompsCarouselProps {
  // necessary to maintain state while component is hidden
  isShowing: boolean;
}

const CompsCarousel: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<CompsCarouselProps>>> = (
  props,
) => {
  const { isShowing } = props;
  // Component state
  const [machineScroll, setMachineScroll] = useState(false);
  const [humanScroll, setHumanScroll] = useState(false);

  // Redux
  const isMobile: boolean = useAppSelector((state) => state.app.device.isMobile);
  const activeId: string = useAppSelector((state) => state.ui.activeId);
  const homeId: string = useAppSelector((state) => state.address.listingId);
  const comparables: Array<ComparablesModelType> = useAppSelector((state) => state.comparables.items);
  const activeFiltersObj: PotentialFilterTypes = getActiveFilters(useAppSelector((state) => state.filter));

  // Hooks
  const parentContainerRef = useRef(null);
  const [itemRefs] = useItemRefs(comparables);

  const { containerRef, homePosition, setHomePosition, handleActiveItem } = useCarousel(parentContainerRef);
  const { checkScrollPosition, handlePrev, handleNext, handleHome, hasPrev, hasNext, showHomeLeft, showHomeRight } =
    useButtons(containerRef, homePosition);
  const { showAlert, handleScrollAlert } = useScrollAlert();
  const prevComparables = usePrevious(comparables);

  // UseEffect
  useEffect(() => {
    // handles scrolling to active item when comparables have not changed
    if (prevComparables && isEqual(prevComparables, comparables)) {
      handleActiveItem(itemRefs[activeId]);
    }
  }, [activeId]);

  useEffect(() => {
    // handles scrolling to my property when comparables change (filter is set)
    setHomePosition(itemRefs[homeId]);
    checkScrollPosition();
    setMachineScroll(true);
  }, [comparables, itemRefs, isMobile]);

  useScrollRefresh(isShowing, itemRefs[activeId], handleActiveItem, checkScrollPosition, setMachineScroll);

  // Callbacks
  const handleScroll = () => {
    /**
     * On initial mount, carousel starts at beginning and then focuses on the active
     * listing... this triggers handleScroll, but this is a machine triggered scroll.
     * ONLY close the window once a human has started scrolling through the carousel.
     */
    const shouldShowAlert = machineScroll && !humanScroll;
    const shouldCloseAlert = !machineScroll && showAlert;

    if (shouldShowAlert) {
      // Show alert only if a human hasn't scrolled the carousel yet!
      handleScrollAlert(true);
      setMachineScroll(false);
    } else if (shouldCloseAlert) {
      // Close alert for good
      setHumanScroll(true);
      handleScrollAlert(false);
    }

    checkScrollPosition();
  };

  const hasActiveFilters = Boolean(Object.keys(activeFiltersObj).length > 0);
  if (comparables.length <= 1) {
    return <NoResults hasActiveFilters={hasActiveFilters} />;
  }

  const Carousel = (
    <S.ItemWrapper ref={containerRef} onScroll={handleScroll}>
      {isMobile && showAlert && <ScrollAlert />}
      <MemoizedWrapper comparables={comparables} itemRefs={itemRefs} />
    </S.ItemWrapper>
  );

  if (!isShowing) {
    return null;
  }

  if (isMobile) {
    return (
      <S.MobileMasterContainer ref={parentContainerRef}>
        <LeftMobileButton showHomeButton={showHomeLeft} homeClickHandler={handleHome} />
        {Carousel}
        <RightMobileButton showHomeButton={showHomeRight} homeClickHandler={handleHome} />
      </S.MobileMasterContainer>
    );
  }

  return (
    <S.MasterContainer ref={parentContainerRef}>
      <LeftDesktopButton
        showScrollButton={hasPrev}
        showHomeButton={showHomeLeft}
        scrollClickHandler={handlePrev}
        homeClickHandler={handleHome}
      />
      {Carousel}
      <RightDesktopButton
        showScrollButton={hasNext}
        showHomeButton={showHomeRight}
        scrollClickHandler={handleNext}
        homeClickHandler={handleHome}
      />
    </S.MasterContainer>
  );
};

export default CompsCarousel;
